import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Sidebar } from "@/components/shell/Sidebar";
import { BottomNav } from "@/components/shell/BottomNav";
import { Navbar } from "@/components/shell/Navbar";
import { Ticker } from "@/components/shell/Ticker";
import { MarketsView } from "@/components/views/MarketsView";
import { MarketDetailView } from "@/components/views/MarketDetailView";
import { PortfolioView } from "@/components/views/PortfolioView";
import { ActivityView } from "@/components/views/ActivityView";
import { ProfileView } from "@/components/views/ProfileView";
import { TradePanel } from "@/components/trade/TradePanel";
import { WalletModal } from "@/components/wallet/WalletModal";
import { DepositModal } from "@/components/wallet/DepositModal";
import { WithdrawModal } from "@/components/wallet/WithdrawModal";
import { SearchOverlay } from "@/components/modals/SearchOverlay";
import { NotificationsPopover } from "@/components/modals/NotificationsPopover";
import { Toast } from "@/components/modals/Toast";
import type { components } from "@/api/schema.gen";

type MarketView = components["schemas"]["MarketView"];

export function App() {
  const view = useStore((s) => s.view);
  // detail page è gestita dentro lo store come `view = "market-detail"` con
  // `detailSlug` come param — niente più modal popup.
  const [tickerMarkets, setTickerMarkets] = useState<MarketView[]>([]);

  useEffect(() => {
    // Carica markets per il ticker (separato dalle viste perché serve sempre).
    let cancelled = false;
    const load = async () => {
      const { data } = await api.GET("/v1/markets", {
        params: { query: { limit: 12, status: "open" } },
      });
      if (!cancelled && data) setTickerMarkets(data.items);
    };
    load();
    const t = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  return (
    // `overflow-x-clip`: se un figlio sfora in larghezza si vede il contenuto
    // tagliato, non tutta la pagina che scorre di lato.
    <div className="flex min-h-screen overflow-x-clip">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Navbar openMarkets={tickerMarkets.length} />
        <Ticker markets={tickerMarkets} mids={{}} />

        <main className="flex-1">
          {(view === "markets" || view === "trending") && <MarketsView />}
          {view === "market-detail" && <MarketDetailView />}
          {view === "portfolio" && <PortfolioView />}
          {view === "activity" && <ActivityView />}
          {view === "profile" && <ProfileView />}
          {["sports", "politics", "crypto", "culture", "science"].includes(view) && (
            <CategoryViewMount key={view} />
          )}
        </main>
      </div>

      <BottomNav />

      <TradePanel />
      <WalletModal />
      <DepositModal />
      <WithdrawModal />
      <SearchOverlay />
      <NotificationsPopover />
      <Toast />
    </div>
  );
}

/** Quando la sidebar seleziona una categoria, riusiamo MarketsView setting
 *  la categoria dello store al mount. Un dettaglio di routing: le view
 *  "sports/politics/..." sono filtri sopra "markets". */
function CategoryViewMount() {
  const view = useStore((s) => s.view);
  const setCategory = useStore((s) => s.setCategory);
  useEffect(() => {
    if (view === "sports") setCategory("sports");
    else if (view === "politics") setCategory("politics");
    else if (view === "crypto") setCategory("crypto");
    else if (view === "culture") setCategory("culture");
    else if (view === "science") setCategory("science");
  }, [view, setCategory]);
  return <MarketsView />;
}
